// Cetak / PDF: a print-only layout of a structured document. It mounts in a
// portal on <body>, marks the body so the print stylesheet (chat.css,
// @media print) hides the app and prints only this sheet on A4, opens the
// browser's print dialog (Save as PDF lives there), and unmounts after it.
// On screen the sheet stays hidden.
import { useEffect } from "react";
import { createPortal } from "react-dom";
import type { StructuredDocument } from "../api";
import { DocumentBlocks } from "./DocumentCard";

export const PRINTING_CLASS = "printing-document";

export function PrintDocument({ document: doc, onDone }: { document: StructuredDocument; onDone: () => void }) {
  useEffect(() => {
    const body = window.document.body;
    body.classList.add(PRINTING_CLASS);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      onDone();
    };
    window.addEventListener("afterprint", finish);
    // Let the sheet render before the dialog opens; print() blocks in most
    // browsers, so returning from it also means the dialog closed.
    const timer = window.setTimeout(() => {
      try {
        if (typeof window.print === "function") window.print();
      } finally {
        finish();
      }
    }, 50);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", finish);
      body.classList.remove(PRINTING_CLASS);
    };
    // onDone is stable enough for one print run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <article className="print-doc" aria-hidden="true">
      <h1 className="print-doc-title">{doc.title.trim() || "Dokumen"}</h1>
      <DocumentBlocks blocks={doc.blocks} print />
    </article>,
    window.document.body,
  );
}
